/**
 * v3.20.0 — /reset-password?token=... page.
 *
 * User lands here from the email link. Reads token from query string,
 * shows a new-password + confirm form, calls POST /api/auth/reset-password,
 * then redirects to /login on success.
 */
import { useState, useMemo } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Lock, Eye, EyeOff, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { describeApiError, readFetchError } from '../lib/apiErrors';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MIN_LEN = 8;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const navigate = useNavigate();

  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const tokenMissing = !token || token.length < 32;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (pw.length < MIN_LEN) {
      setError(`Password must be at least ${MIN_LEN} characters.`);
      return;
    }
    if (pw !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: pw }),
      });
      if (!res.ok) {
        const msg = await readFetchError(res, `HTTP ${res.status}`);
        throw new Error(msg);
      }
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      setError(describeApiError(err, 'Could not reset password. Try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-slate-200 shadow-sm" data-testid="reset-password-card">
        <CardHeader className="space-y-1">
          <CardTitle
            className="text-2xl font-semibold text-slate-900"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Set a new password
          </CardTitle>
          <p className="text-sm text-slate-500">
            Pick something strong. You'll use this the next time you sign in.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {tokenMissing ? (
            <div
              className="rounded-lg border border-rose-200 bg-rose-50 p-4"
              data-testid="reset-token-missing"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-rose-900">
                  <p className="font-medium mb-1">Missing or invalid reset link.</p>
                  <p>
                    This page needs a valid reset token. Use the link from the email we sent
                    you, or{' '}
                    <Link
                      to="/forgot-password"
                      className="underline font-medium text-rose-800 hover:text-rose-900"
                    >
                      request a new one
                    </Link>
                    .
                  </p>
                </div>
              </div>
            </div>
          ) : success ? (
            <div
              className="rounded-lg border border-emerald-200 bg-emerald-50 p-4"
              data-testid="reset-success"
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-emerald-900">
                  <p className="font-medium mb-1">Password reset.</p>
                  <p>You'll be redirected to sign in…</p>
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="pw" className="text-slate-700">
                  New password
                </Label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="pw"
                    type={showPw ? 'text' : 'password'}
                    placeholder={`At least ${MIN_LEN} characters`}
                    value={pw}
                    onChange={(e) => {
                      setPw(e.target.value);
                      setError(null);
                    }}
                    autoFocus
                    autoComplete="new-password"
                    className="pl-9 pr-10 border-slate-200 focus:border-teal-600 focus:ring-teal-600/20"
                    data-testid="reset-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    tabIndex={-1}
                    aria-label={showPw ? 'Hide password' : 'Show password'}
                    data-testid="reset-password-toggle-visibility"
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm" className="text-slate-700">
                  Confirm new password
                </Label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="confirm"
                    type={showPw ? 'text' : 'password'}
                    placeholder="Type it again"
                    value={confirm}
                    onChange={(e) => {
                      setConfirm(e.target.value);
                      setError(null);
                    }}
                    autoComplete="new-password"
                    className="pl-9 border-slate-200 focus:border-teal-600 focus:ring-teal-600/20"
                    data-testid="reset-password-confirm-input"
                  />
                </div>
              </div>

              {error && (
                <div
                  className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
                  data-testid="reset-password-error"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-teal-700 hover:bg-teal-800 text-white"
                data-testid="reset-password-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Setting password…
                  </>
                ) : (
                  'Reset password'
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
